import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PokemonCard from '../components/PokemonCard';
import SmartSearchBar from '../components/SmartSearchBar';
import { PageContainer, SectionHeader } from '../components/shared';
import { useLanguage } from '../contexts/LanguageContext';
import { getApiBaseUrl } from '../utils/apiUrl';
import { 
  ChevronDown, 
  Loader2, 
  RefreshCw, 
  AlertCircle,
  Search,
  Grid3X3,
  List,
  SortAsc,
  SortDesc
} from 'lucide-react';

const Home = () => {
  const { t } = useLanguage();
  const [pokemon, setPokemon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchPokemon = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const { page = 1, search = '', sortBy: paramSortBy = sortBy, sortOrder: paramSortOrder = sortOrder, limit = 12 } = params;
      const searchParam = encodeURIComponent(search || searchQuery || '');
      const response = await fetch(`${getApiBaseUrl()}/api/pokemon?page=${page}&search=${searchParam}&sortBy=${paramSortBy}&sortOrder=${paramSortOrder}&limit=${limit}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        if (page > 1) {
          setPokemon(prev => [...prev, ...data.data]);
        } else {
          setPokemon(data.data);
        }
        // Ensure pagination object has all required fields
        if (data.pagination) {
          setPagination({
            ...data.pagination,
            showing: data.pagination.showing || `${data.data.length} / ${data.pagination.totalItems || 0}`,
            currentPage: data.pagination.currentPage || page,
            hasNextPage: data.pagination.hasNextPage !== undefined ? data.pagination.hasNextPage : (data.pagination.currentPage || page) < (data.pagination.totalPages || 1)
          });
        }
      } else {
        setError(data.error || 'Failed to fetch Pokemon');
      }
    } catch (err) {
      console.error('Error fetching Pokemon:', err);
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, searchQuery]);


  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (!isMounted) return;
      
      try {
        console.log('🔄 Loading initial data...');
        setLoading(true);
        const searchParam = encodeURIComponent(searchQuery || '');
        const response = await fetch(`${getApiBaseUrl()}/api/pokemon?page=1&search=${searchParam}&sortBy=${sortBy}&sortOrder=${sortOrder}&limit=12`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!isMounted) return;
        
        if (data.success) {
          setPokemon(data.data);
          if (data.pagination) {
            setPagination({
              ...data.pagination,
              showing: data.pagination.showing || `${data.data.length} / ${data.pagination.totalItems || 0}`,
              currentPage: data.pagination.currentPage || 1,
              hasNextPage: data.pagination.hasNextPage !== undefined ? data.pagination.hasNextPage : (data.pagination.currentPage || 1) < (data.pagination.totalPages || 1)
            });
          }
        } else {
          setError(data.error || 'Failed to fetch Pokemon');
        }
        console.log('✅ Initial data loaded successfully');
      } catch (error) {
        if (!isMounted) return;
        console.error('❌ Failed to load initial data:', error);
        setError(error.message || 'Network error');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [sortBy, sortOrder, searchQuery]);

  const handleLoadMore = useCallback(async () => {
    if (!pagination || loading) {
      console.log('Cannot load more:', { pagination: !!pagination, loading });
      return;
    }
    
    const nextPage = (pagination.currentPage || 1) + 1;
    const hasNext = pagination.hasNextPage !== undefined 
      ? pagination.hasNextPage 
      : nextPage <= (pagination.totalPages || 1);
    
    if (!hasNext) {
      console.log('No more pages to load');
      return;
    }
    
    try {
      setLoading(true);
      const searchParam = encodeURIComponent(searchQuery || '');
      const response = await fetch(`${getApiBaseUrl()}/api/pokemon?page=${nextPage}&search=${searchParam}&sortBy=${sortBy}&sortOrder=${sortOrder}&limit=12`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        const previousLength = pokemon.length;
        setPokemon(prev => {
          const newList = [...prev, ...data.data];
          return newList;
        });
        if (data.pagination) {
          setPagination({
            ...data.pagination,
            showing: data.pagination.showing || `${(pagination.currentPage || 1) * 12 + data.data.length} / ${data.pagination.totalItems || 0}`,
            currentPage: data.pagination.currentPage || nextPage,
            hasNextPage: data.pagination.hasNextPage !== undefined ? data.pagination.hasNextPage : (data.pagination.currentPage || nextPage) < (data.pagination.totalPages || 1)
          });
        }
        console.log('✅ Loaded more Pokemon:', data.data.length);
        
        // Scroll to the first new Pokemon after a short delay to allow DOM update
        setTimeout(() => {
          const firstNewPokemon = document.querySelector(`[data-pokemon-index="${previousLength}"]`);
          if (firstNewPokemon) {
            // Scroll to show the new Pokemon, keeping some context above
            firstNewPokemon.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 200);
      } else {
        setError(data.error || 'Failed to load more Pokemon');
      }
    } catch (err) {
      console.error('Error loading more Pokemon:', err);
      setError(err.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [pagination, sortBy, sortOrder, searchQuery, loading]);
  
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await fetchPokemon({ limit: 12, sortBy, sortOrder, search: searchQuery });
      setRetryCount(0);
    } catch (error) {
      console.error('Failed to refresh:', error);
      setRetryCount(prev => prev + 1);
    } finally {
      setIsRefreshing(false);
    }
  }, [sortBy, sortOrder, searchQuery, fetchPokemon]);
  
  const handleSort = useCallback((newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  }, [sortBy]);
  
  const handleRetry = useCallback(() => {
    if (retryCount < 3) {
      handleRefresh();
    } else {
      // Reset retry count and try again
      setRetryCount(0);
      handleRefresh();
    }
  }, [retryCount, handleRefresh]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  if (error && retryCount >= 3) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-lg mx-auto p-8 bg-white rounded-2xl shadow-xl"
        >
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">{t('home.errorTitle')}</h2>
          <p className="text-red-600 mb-2 font-medium">{error}</p>
          <p className="text-sm text-gray-500 mb-6">
            {t('home.errorMessage')}
          </p>
          <div className="space-y-3">
            <button 
              onClick={handleRetry}
              className="btn-primary w-full flex items-center justify-center"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('home.retry')}
            </button>
            <button 
              onClick={() => window.location.reload()}
              className="btn-secondary w-full"
            >
              {t('home.refreshPage')}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageContainer maxWidth="7xl" padding={true} className="py-8">
        <div className="space-y-12">
          {/* Hero Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center py-12 relative overflow-hidden"
          >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-20 h-20 bg-blue-500 rounded-full"></div>
          <div className="absolute top-32 right-20 w-16 h-16 bg-purple-500 rounded-full"></div>
          <div className="absolute bottom-20 left-1/4 w-12 h-12 bg-pink-500 rounded-full"></div>
          <div className="absolute bottom-32 right-1/3 w-14 h-14 bg-yellow-500 rounded-full"></div>
        </div>
        
        <div className="max-w-4xl mx-auto relative z-10">
          <motion.div
            initial={{ scale: 0.8, rotate: -10 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl p-2"
          >
            <img 
              src="/logo-pogodex.png" 
              alt="Pokedex Logo" 
              className="w-full h-full object-contain"
            />
          </motion.div>
          
          <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            Pokedex
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {t('home.subtitle')}
          </p>
          
          <div className="max-w-2xl mx-auto px-4">
            <SmartSearchBar 
              placeholder={t('home.search.placeholder')}
              className="w-full"
            />
          </div>
        </div>
      </motion.section>


      {/* Controls Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-white rounded-2xl p-6 shadow-pokemon mb-8"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold text-gray-800">{t('home.featured')}</h2>
            {pagination && (
              <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                {pagination.showing}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'
                }`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
            
            {/* Sort Options */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSort('id')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  sortBy === 'id' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                ID
                {sortBy === 'id' && (
                  sortOrder === 'asc' ? <SortAsc className="w-3 h-3 ml-1" /> : <SortDesc className="w-3 h-3 ml-1" />
                )}
              </button>
              <button
                onClick={() => handleSort('name_en')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  sortBy === 'name_en' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('home.sortName')}
                {sortBy === 'name_en' && (
                  sortOrder === 'asc' ? <SortAsc className="w-3 h-3 ml-1" /> : <SortDesc className="w-3 h-3 ml-1" />
                )}
              </button>
              <button
                onClick={() => handleSort('max_cp')}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  sortBy === 'max_cp' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                CP
                {sortBy === 'max_cp' && (
                  sortOrder === 'asc' ? <SortAsc className="w-3 h-3 ml-1" /> : <SortDesc className="w-3 h-3 ml-1" />
                )}
              </button>
            </div>
            
            {/* Refresh Button */}
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </motion.section>
      
      {/* Featured Pokemon Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.5 }}
      >

        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="space-y-8"
            >
              {/* Enhanced Loading States */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white rounded-2xl p-6 shadow-lg"
                  >
                    <div className="h-48 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl mb-4 animate-pulse"></div>
                    <div className="space-y-3">
                      <div className="h-6 bg-gray-200 rounded-lg animate-pulse"></div>
                      <div className="h-4 bg-gray-200 rounded-lg w-3/4 animate-pulse"></div>
                      <div className="flex gap-2">
                        <div className="h-6 bg-gray-200 rounded-full w-16 animate-pulse"></div>
                        <div className="h-6 bg-gray-200 rounded-full w-20 animate-pulse"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded w-5/6 animate-pulse"></div>
                        <div className="h-4 bg-gray-200 rounded w-4/6 animate-pulse"></div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={`grid gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                  : 'grid-cols-1'
              }`}
            >
              <AnimatePresence>
                {pokemon && Array.isArray(pokemon) && pokemon.length > 0 ? pokemon.map((pokemon, index) => (
                  <motion.div
                    key={pokemon.Pokemon_ID}
                    data-pokemon-index={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                    layout
                  >
                    <PokemonCard 
                      pokemon={pokemon} 
                      index={index} 
                      viewMode={viewMode}
                    />
                  </motion.div>
                )) : (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="col-span-full text-center py-16"
                  >
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <Search className="w-12 h-12 text-gray-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">{t('home.noResults')}</h3>
                    <p className="text-gray-500 mb-6">{t('home.noResultsHint')}</p>
                    <button
                      onClick={handleRefresh}
                      className="btn-primary"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {t('home.refresh')}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Load More Button */}
        <AnimatePresence>
          {pagination && pagination.hasNextPage && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center mt-8"
            >
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    {t('common.loading')}
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-5 h-5 mr-2" />
                    {t('home.loadMore')}
                  </>
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.section>

        </div>
      </PageContainer>
    </div>
  );
};

export default Home;
